import { FiChevronLeft } from 'react-icons/fi';
import OverviewWidget from './OverviewWidget';
import ReportFilter from 'app/components/ReportFilter';
import moment from 'moment';
import { useEffect, useMemo, useState } from 'react';
import { ApiClient, useClient } from 'jsonapi-react';
import toast from 'react-hot-toast';
import { useFormik } from 'formik';
import LiveClassesTab from './LiveClassesTab';
import RegistrationTab from './RegistrationTab';
import { navigate } from '@reach/router';

export default function GeneralRegistrationReport() {
  const [activeTab, setActiveTab] = useState('');
  const [kpis, setKpis] = useState([]);
  const [regionals, setRegionals] = useState([]);
  const [selectedRegionals, setSelectedRegionals] = useState([]);
  const [institutions, setInstitutions] = useState([]);
  const [selectedInstitutions, setSelectedInstitutions] = useState([]);
  const [currentFilters, setCurrentFilters] = useState([]);
  const [selectedSeries, setSelectedSeries] = useState([]);
  const [loadingKpis, setLoadingKpis] = useState(false);
  const apiSso = process.env.REACT_APP_SSO_API_URL;
  const params = new URLSearchParams(location.search);
  const tipo = params.get('tipo');

  useEffect(() => {
    if (!tipo) {
      navigate('/relatorio/adesao?tipo=cadastro');
    }
  }, []);

  const ssoClient = new ApiClient({
    url: `${apiSso}/api/v1`
  });

  const client = useClient();

  const getLiveClassesKpis = async () => {
    setLoadingKpis(true);
    try {
      const { data } = await ssoClient.fetch('/live_classroom_accesses/kpis');
      const kpis = [
        { id: 1, title: 'Componente mais acessado', value: data['most-accessed-component'], variation: 'report-kpi-card--users' },
        { id: 2, title: 'Total de acessos a Aulas ao Vivo', value: data['total-accesses'], variation: 'report-kpi-card--user' },
        { id: 3, title: 'Maior acesso', value: data['most-accessed-source'], variation: 'report-kpi-card--user' }
      ];
      setKpis(kpis);
    } catch (error) {
      toast.error('Erro ao buscar os KPIs de aulas ao vivo.');
    }
    setLoadingKpis(false);
  };

  const getRegistrationsKpis = async () => {
    setLoadingKpis(true);
    try {
      const { data } = await ssoClient.fetch('/institution_infos/kpis');
      const kpis = [
        { id: 1, title: 'Total de cadastros', value: data['total'], variation: 'report-kpi-card--check' },
        { id: 2, title: 'Alunos no iSEDUC', value: data['total-iseduc'], variation: 'report-kpi-card--users' }
      ];
      setKpis(kpis);
    } catch (error) {
      toast.error('Erro ao buscar os KPIs de cadastros.');
    }
    setLoadingKpis(false);
  };

  const filters = useFormik({
    initialValues: {
      'start-date': moment().subtract(1, 'month').format('YYYY-MM-DD'),
      'end-date': moment().format('YYYY-MM-DD')
    }
  });

  const fetchRegionals = async (url = '/regionals_for_filter') => {
    const { data, error } = await ssoClient.fetch(url);
    if (error) {
      toast.error('Falha ao carregar regionais');
    } else {
      setRegionals(data.map(item => ({ value: item.id, label: item.name })));
    }
  };

  const fetchInstitutions = async (url = '/institutions_for_filter') => {
    const { data, error } = await ssoClient.fetch(url);
    if (error) {
      toast.error('Falha ao carregar instituições');
    } else {
      setInstitutions(data.map(item => ({ value: item.inep, label: item.name, gre: item.gre })));
    }
  };

  const data = useMemo(() => {
    return [
      {
        placeholder: 'Digite o nome da Regional',
        name: 'Regional',
        options: regionals
      },
      {
        placeholder: 'Digite o nome da instituição',
        name: 'Instituição',
        options: institutions
      },
      ...(activeTab === 'live-classes'
        ? [
            {
              placeholder: 'Digite o nome da série',
              name: 'Série',
              options: [
                { value: 'EM1', label: '1º ano', type: 'serie' },
                { value: 'EM2', label: '2º ano', type: 'serie' },
                { value: 'EM3', label: '3º ano', type: 'serie' },
                { value: 'EJA', label: 'EJA', type: 'serie' }
              ]
            }
          ]
        : [])
    ];
  }, [institutions, regionals, activeTab]);

  useEffect(() => {
    if (tipo === 'cadastro') {
      setActiveTab('registrations');
    } else if (tipo === 'aulas-ao-vivo') {
      setActiveTab('live-classes');
    }
  }, [tipo]);

  useEffect(() => {
    setSelectedInstitutions([]);
    setSelectedRegionals([]);
    setSelectedSeries([]);

    filters.setFieldValue('start-date', moment().subtract(1, 'month').format('YYYY-MM-DD'));
    filters.setFieldValue('end-date', moment().format('YYYY-MM-DD'));

    if (activeTab === 'registrations') {
      getRegistrationsKpis();
    } else if (activeTab === 'live-classes') {
      getLiveClassesKpis();
    }
  }, [activeTab]);

  useEffect(() => {
    setCurrentFilters([...selectedRegionals, ...selectedInstitutions, ...selectedSeries]);
  }, [selectedRegionals, selectedInstitutions, selectedSeries]);

  useEffect(() => {
    Promise.all([fetchRegionals(), fetchInstitutions()]).then(() => {});
  }, []);

  return (
    <div className="report">
      <header className="report__header">
        <h1 className="report__title">
          <button
            className="card__icon-wrapper"
            aria-label="Voltar"
            title="Voltar"
            onClick={() => navigate('/metricas/institucional')}
          >
            <FiChevronLeft className="card__icon" />
          </button>
          Relatório de cadastros e acessos a Aulas ao Vivo
        </h1>
      </header>

      <div className="flex gap-3">
        <div
          className="report-tab !pl-32 !pr-0"
          onClick={() => {
            setActiveTab('registrations');
            navigate('/relatorio/adesao?tipo=cadastro');
          }}
        >
          <button className={`report-tab__link ${activeTab === 'registrations' ? 'report-tab__link--is-active' : ''}`}>Cadastro</button>
        </div>
        <div
          className="report-tab !px-0"
          onClick={() => {
            setActiveTab('live-classes');
            navigate('/relatorio/adesao?tipo=aulas-ao-vivo');
          }}
        >
          <button className={`report-tab__link ${activeTab === 'live-classes' ? 'report-tab__link--is-active' : ''}`}>Acessos - Aulas ao vivo</button>
        </div>
      </div>

      <main className="report__body">
        {!loadingKpis && kpis.length > 0 && <OverviewWidget kpis={kpis} />}
        <div className="report__filter report__filter--space report__filter--multi-select">
          <ReportFilter
            setSelectedRegionals={setSelectedRegionals}
            selectedRegionals={selectedRegionals}
            setSelectedInstitutions={setSelectedInstitutions}
            selectedInstitutions={selectedInstitutions}
            fetchInstitutions={fetchInstitutions}
            currentFilters={currentFilters}
            setCurrentFilters={setCurrentFilters}
            selectedSeries={selectedSeries}
            setSelectedSeries={setSelectedSeries}
            data={data}
            ssoClient={true}
          />
          {activeTab === 'live-classes' && (
            <div className="report-form__datepicker">
              <input
                className="report-form__control"
                id="start-date"
                name="start-date"
                type="date"
                value={moment(filters.values['start-date']).format('YYYY-MM-DD')}
                onChange={e => filters.setFieldValue('start-date', e.target.value)}
                title="Data de início"
                aria-label="Data de início"
                min={moment().startOf('year').format('YYYY-MM-DD')}
              />
              <input
                className="report-form__control"
                id="end-date"
                name="end-date"
                type="date"
                value={moment(filters.values['end-date']).format('YYYY-MM-DD')}
                onChange={e => filters.setFieldValue('end-date', e.target.value)}
                min={moment(filters.values['start-date']).format('YYYY-MM-DD')}
                title="Data de fim"
                aria-label="Data de fim"
              />
            </div>
          )}
        </div>
        {activeTab === 'registrations' && (
          <RegistrationTab
            filters={filters}
            currentFilters={currentFilters}
            institutions={institutions}
          />
        )}
        {activeTab === 'live-classes' && (
          <LiveClassesTab
            filters={filters}
            currentFilters={currentFilters}
          />
        )}
      </main>
    </div>
  );
}
